/**
 * useSageChat - React Hook for Sage Chat Agent
 *
 * Connects to the Sage chat API with SSE streaming, tool execution display,
 * and full conversation management including history.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdvisorSafe } from '@/contexts/AdvisorContext';
import { gtm } from '@/lib/gtm';
import { trackEvent } from '@/lib/posthog';
import { sageEvents } from '@/lib/analytics/events/sage';
import { isUsageLimitHttpError } from '@/lib/usageLimitError';

// ============================================================================
// Types
// ============================================================================

export type MessageRole = 'user' | 'assistant' | 'system';

export interface ToolCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
  status: 'pending' | 'running' | 'completed' | 'failed';
  result?: string;
  duration?: number;
}

export interface DataSource {
  type: 'financial' | 'correspondence' | 'strategy' | 'deadline' | 'goal' | 'children' | 'asset' | 'debt' | 'state_guidelines';
  id: string;
  title: string;
  relevance?: number;
  /** Date associated with this source */
  date?: string;
  /** Category or subtype */
  category?: string;
  /** App route path to navigate to this source */
  routePath?: string;
  /** Brief excerpt or preview */
  excerpt?: string;
}

// Attachment types
export type ImageMediaType = 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';

export interface ImageAttachment {
  type: 'image';
  data: string; // base64 encoded
  mediaType: ImageMediaType;
  filename?: string;
}

export interface DocumentReference {
  type: 'document_reference';
  documentId: string;
  documentType: 'financial_document' | 'correspondence' | 'email_thread';
  pageNumber?: number;
}

export type Attachment = ImageAttachment | DocumentReference;

// Message attachment for display
export interface MessageAttachment {
  id: string;
  type: 'image' | 'document_reference';
  url?: string; // For display (data URL for images)
  filename?: string;
  documentTitle?: string;
}

export interface SageMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: Date;
  toolCalls?: ToolCall[];
  sources?: DataSource[];
  attachments?: MessageAttachment[];
  isStreaming?: boolean;
}

export interface HistoryMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  tool_calls?: unknown[];
  sources?: unknown[];
  created_at: string;
}

export interface UseSageChatOptions {
  onError?: (error: string) => void;
  onToolCall?: (toolCall: ToolCall) => void;
  onSourcesFound?: (sources: DataSource[]) => void;
  onConversationCreated?: (conversationId: string) => void;
  onMessageComplete?: () => void;
  /** Called when the server returns a usage limit error (403/429). Opens the upgrade modal. */
  onUsageLimitHit?: () => void;
}

export interface UseSageChatReturn {
  messages: SageMessage[];
  isStreaming: boolean;
  isThinking: boolean;
  currentToolCall: ToolCall | null;
  error: string | null;
  conversationId: string | null;
  isLoadingMessages: boolean;
  sendMessage: (content: string, attachments?: Attachment[]) => Promise<void>;
  stopMessage: () => void;
  retry: () => void;
  clearHistory: () => void;
  clearError: () => void;
  loadConversation: (id: string, historyMessages: HistoryMessage[]) => void;
  startNewConversation: () => void;
}

// ============================================================================
// Configuration
// ============================================================================

// Cloudflare Worker URL (deployed)
const SAGE_API_URL = 'https://sage-chat.scott-4bd.workers.dev/v2/chat';

// ============================================================================
// Hook Implementation
// ============================================================================

export function useSageChat(options: UseSageChatOptions = {}): UseSageChatReturn {
  const { session } = useAuth();
  // When an advisor is viewing a client's case, tell the worker which client so
  // it loads (and authorizes) that case and adopts the advisor-facing persona.
  // useAdvisorSafe returns inert defaults on client routes (no AdvisorProvider).
  const { isAdvisorMode, selectedClientId } = useAdvisorSafe();
  const [messages, setMessages] = useState<SageMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [currentToolCall, setCurrentToolCall] = useState<ToolCall | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Stabilize options ref to prevent infinite re-render loops when callers
  // pass inline objects (new reference every render)
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const lastMessageRef = useRef<string>('');
  const isFirstConversationRef = useRef(true);
  const sendStartTimeRef = useRef<number>(0);
  const lastInputTokensRef = useRef<number | undefined>();
  const lastOutputTokensRef = useRef<number | undefined>();

  /**
   * Load a conversation from history
   */
  const loadConversation = useCallback((id: string, historyMessages: HistoryMessage[]) => {
    console.log('[useSageChat] loadConversation called with id:', id, 'messages:', historyMessages.length);
    setIsLoadingMessages(true);

    // Convert history messages to SageMessage format
    const sageMessages: SageMessage[] = historyMessages.map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: new Date(msg.created_at),
      toolCalls: msg.tool_calls as ToolCall[] | undefined,
      sources: msg.sources as DataSource[] | undefined,
      isStreaming: false,
    }));

    console.log('[useSageChat] Converted to sageMessages:', sageMessages.length);
    setMessages(sageMessages);
    setConversationId(id);
    setError(null);
    setIsLoadingMessages(false);
    isFirstConversationRef.current = false;
    console.log('[useSageChat] State updated, conversation loaded');
  }, []);

  /**
   * Start a new conversation (clear current and generate new ID)
   */
  const startNewConversation = useCallback(() => {
    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setMessages([]);
    setConversationId(null);
    setError(null);
    setIsStreaming(false);
    setIsThinking(false);
    setCurrentToolCall(null);
    isFirstConversationRef.current = true;
  }, []);

  /**
   * Send a message to Sage
   */
  const sendMessage = useCallback(
    async (content: string, attachments?: Attachment[]) => {
      console.log('[SAGE-CLIENT] sendMessage called with content:', content?.slice(0, 50), 'attachments:', attachments?.length || 0);
      if (!content.trim() || !session?.access_token) {
        console.log('[SAGE-CLIENT] Early return - content empty or no session. content:', !!content.trim(), 'session:', !!session?.access_token);
        setError('Unable to send message. Please ensure you are logged in.');
        return;
      }

      // Store for retry
      lastMessageRef.current = content;

      // Abort any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Clear previous error
      setError(null);

      // Convert attachments to display format
      const messageAttachments: MessageAttachment[] = (attachments || []).map((att, idx) => {
        if (att.type === 'image') {
          return {
            id: `attachment-${Date.now()}-${idx}`,
            type: 'image' as const,
            url: `data:${att.mediaType};base64,${att.data}`,
            filename: att.filename,
          };
        } else {
          return {
            id: `attachment-${Date.now()}-${idx}`,
            type: 'document_reference' as const,
            documentTitle: att.documentId, // TODO: Fetch actual title
          };
        }
      });

      // Optimistically add user message
      const userMessage: SageMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date(),
        attachments: messageAttachments.length > 0 ? messageAttachments : undefined,
      };
      console.log('[SAGE-CLIENT] Adding user message:', userMessage.id, 'attachments:', messageAttachments.length);
      setMessages((prev) => [...prev, userMessage]);
      // GTM conversion tracking - track Sage chat usage
      gtm.trackSageChatMessage();

      // Add placeholder assistant message
      const assistantMessageId = `assistant-${Date.now()}`;
      const assistantMessage: SageMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
        toolCalls: [],
        sources: [],
      };
      console.log('[SAGE-CLIENT] Adding assistant placeholder:', assistantMessageId);
      setMessages((prev) => [...prev, assistantMessage]);

      setIsStreaming(true);
      setIsThinking(true);
      sendStartTimeRef.current = Date.now();
      lastInputTokensRef.current = undefined;
      lastOutputTokensRef.current = undefined;

      try {
        console.log('[SAGE-CLIENT] Sending request to:', SAGE_API_URL);
        const response = await fetch(SAGE_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            message: content,
            conversationId,
            includeThinking: false,
            attachments: attachments || [],
            // Advisor mode: scope the chat to the selected client's case.
            ...(isAdvisorMode && selectedClientId ? { clientId: selectedClientId } : {}),
          }),
          signal: abortControllerRef.current.signal,
        });

        console.log('[SAGE-CLIENT] Response received:', response.status, response.headers.get('content-type'));
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[SAGE-CLIENT] Response not ok:', errorText);

          // Check for usage limit errors (403/429) - trigger upgrade modal instead of generic error
          if (isUsageLimitHttpError(response.status, errorText)) {
            console.log('[SAGE-CLIENT] Usage limit hit, triggering upgrade modal');
            // Remove the placeholder assistant message
            setMessages((prev) => prev.filter((m) => m.id !== assistantMessageId));
            setIsStreaming(false);
            setIsThinking(false);
            optionsRef.current.onUsageLimitHit?.();
            return;
          }

          throw new Error(`Request failed: ${response.status}`);
        }

        // Process SSE stream
        console.log('[SAGE-CLIENT] Starting SSE stream processing');
        await processSSEStream(response, assistantMessageId);
        console.log('[SAGE-CLIENT] SSE stream processing complete');
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Request was aborted, don't show error
          return;
        }

        const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);
        optionsRef.current.onError?.(errorMessage);

        // Track error analytics
        const errorEventData = sageEvents.errorOccurred({ error_message_preview: errorMessage });
        trackEvent(errorEventData.event, errorEventData.properties);

        // Remove failed assistant message
        setMessages((prev) => prev.filter((m) => m.id !== assistantMessageId));
      } finally {
        setIsStreaming(false);
        setIsThinking(false);
        setCurrentToolCall(null);
      }
    },
    [session?.access_token, conversationId, isAdvisorMode, selectedClientId]
  );

  /**
   * Process SSE stream from the server
   */
  const processSSEStream = async (response: Response, messageId: string) => {
    console.log('[SAGE-CLIENT] processSSEStream started');
    if (!response.body) {
      console.error('[SAGE-CLIENT] No response body!');
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';

    // Watchdog: if the worker stalls (connects but stops sending), don't spin
    // forever — time out so the UI can surface an error and the user can retry.
    const STREAM_IDLE_TIMEOUT_MS = 30000;
    const readWithTimeout = () =>
      Promise.race([
        reader.read(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Sage timed out — please try again.')), STREAM_IDLE_TIMEOUT_MS),
        ),
      ]) as ReturnType<typeof reader.read>;

    try {
      while (true) {
        const { done, value } = await readWithTimeout();
        if (done) {
          console.log('[SAGE-CLIENT] Stream done');
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        console.log('[SAGE-CLIENT] Received chunk:', chunk.slice(0, 200));
        buffer += chunk;

        // Parse SSE lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
            console.log('[SAGE-CLIENT] Event:', currentEvent);
            continue;
          }

          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6).trim();
            if (dataStr === '[DONE]') {
              console.log('[SAGE-CLIENT] Received [DONE]');
              continue;
            }

            let data: Record<string, unknown>;
            try {
              data = JSON.parse(dataStr);
            } catch (e) {
              console.error('[SAGE-CLIENT] JSON parse error:', e, dataStr);
              continue;
            }

            // The worker reports failures via an `event: error` frame. Previously
            // the parser discarded the event type, so this payload matched no
            // field in handleSSEEvent and was silently dropped — leaving the
            // thinking spinner stuck forever. Surface it so the user sees an
            // error and can retry.
            if (currentEvent === 'error') {
              const msg =
                (typeof data.message === 'string' && data.message) ||
                'Sage is unavailable right now. Please try again.';
              console.error('[SAGE-CLIENT] Stream error event:', msg);
              throw new Error(msg);
            }

            console.log('[SAGE-CLIENT] Parsed data:', Object.keys(data));
            handleSSEEvent(data, messageId);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  };

  /**
   * Handle individual SSE events
   */
  const handleSSEEvent = (data: Record<string, unknown>, messageId: string) => {
    console.log('[SAGE-CLIENT] handleSSEEvent:', data);

    // Conversation ID
    if (data.conversationId) {
      console.log('[SAGE-CLIENT] Setting conversation ID:', data.conversationId);
      const newConvId = data.conversationId as string;
      setConversationId(newConvId);

      // Notify if this is a newly created conversation
      if (isFirstConversationRef.current) {
        isFirstConversationRef.current = false;
        optionsRef.current.onConversationCreated?.(newConvId);
      }
    }

    // Thinking indicator
    if (data.thinking !== undefined) {
      console.log('[SAGE-CLIENT] Setting thinking');
      setIsThinking(true);
    }

    // Content streaming
    if (data.content !== undefined) {
      console.log('[SAGE-CLIENT] Adding content:', (data.content as string).slice(0, 50));
      setIsThinking(false);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, content: m.content + (data.content as string) }
            : m
        )
      );
    }

    // Tool use started
    if (data.id && data.name && data.input) {
      const toolCall: ToolCall = {
        id: data.id as string,
        name: data.name as string,
        input: data.input as Record<string, unknown>,
        status: 'running',
      };

      setCurrentToolCall(toolCall);
      optionsRef.current.onToolCall?.(toolCall);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, toolCalls: [...(m.toolCalls || []), toolCall] }
            : m
        )
      );
    }

    // Tool result
    if (data.id && data.result !== undefined) {
      const toolId = data.id as string;
      const result = data.result as string;
      const duration = (data.duration as number) || 0;

      setCurrentToolCall(null);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                toolCalls: m.toolCalls?.map((tc) =>
                  tc.id === toolId
                    ? { ...tc, status: 'completed' as const, result, duration }
                    : tc
                ),
              }
            : m
        )
      );
    }

    // Sources
    if (data.sources && Array.isArray(data.sources)) {
      const sources = data.sources as DataSource[];
      optionsRef.current.onSourcesFound?.(sources);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, sources } : m
        )
      );
    }

    // Usage (for analytics) - store tokens for messageReceived event
    if (data.inputTokens !== undefined || data.outputTokens !== undefined) {
      if (data.inputTokens !== undefined) lastInputTokensRef.current = data.inputTokens as number;
      if (data.outputTokens !== undefined) lastOutputTokensRef.current = data.outputTokens as number;
    }

    // Done - finalize message and track analytics
    if (data.messageId) {
      setMessages((prev) => {
        const msg = prev.find((m) => m.id === messageId);
        const responseTimeMs = Date.now() - sendStartTimeRef.current;
        const toolCallsCount = msg?.toolCalls?.length || 0;
        const sourcesCount = msg?.sources?.length || 0;
        const sourceTypes = [...new Set(msg?.sources?.map((s) => s.type) || [])];

        const eventData = sageEvents.messageReceived({
          response_time_ms: responseTimeMs,
          tool_calls_count: toolCallsCount,
          sources_count: sourcesCount,
          source_types: sourceTypes,
          has_error: false,
          input_tokens: lastInputTokensRef.current,
          output_tokens: lastOutputTokensRef.current,
        });
        trackEvent(eventData.event, eventData.properties);

        return prev.map((m) =>
          m.id === messageId
            ? { ...m, id: data.messageId as string, isStreaming: false }
            : m
        );
      });

      // Notify caller that a message was successfully completed
      optionsRef.current.onMessageComplete?.();
    }

    // Error
    if (data.message && data.code) {
      const sseErrorMessage = data.message as string;
      setError(sseErrorMessage);
      optionsRef.current.onError?.(sseErrorMessage);

      const sseErrorEventData = sageEvents.errorOccurred({ error_message_preview: sseErrorMessage });
      trackEvent(sseErrorEventData.event, sseErrorEventData.properties);
    }
  };

  /**
   * Retry the last failed message
   */
  const retry = useCallback(() => {
    if (lastMessageRef.current) {
      // Remove failed messages first
      setMessages((prev) => {
        // Find last user message index
        const lastUserIdx = prev.findLastIndex((m) => m.role === 'user');
        if (lastUserIdx >= 0) {
          return prev.slice(0, lastUserIdx);
        }
        return prev;
      });

      sendMessage(lastMessageRef.current);
    }
  }, [sendMessage]);

  /**
   * Stop the current streaming response
   */
  const stopMessage = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  /**
   * Clear conversation history
   */
  const clearHistory = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  /**
   * Clear current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    messages,
    isStreaming,
    isThinking,
    currentToolCall,
    error,
    conversationId,
    isLoadingMessages,
    sendMessage,
    stopMessage,
    retry,
    clearHistory,
    clearError,
    loadConversation,
    startNewConversation,
  };
}

export default useSageChat;
